import { cn } from '../../lib/cn'
import { totalLabel } from './scorecardTotals'
import type { CourseHole, HoleScore } from '../../lib/golfRound'

/** One player's card, as far as the table is concerned. */
export interface RoundScorecardPlayer {
  id: string
  /** `round_players.position`, 1-4 — the seat, and the order rows are drawn in. */
  position: number
  displayName: string
  /** `user_id === null` on the wire; chipped rather than hidden. */
  guest?: boolean
  /** The reading member's own row. Tinted AND announced. */
  isYou?: boolean
  /**
   * `declined_at !== null` — a seat that said no. STILL SHOWN: a fourball of
   * three is a lie about who was there, and the club never edits the day.
   */
  declined?: boolean
  scores: readonly HoleScore[]
}

export interface RoundScorecardProps {
  /**
   * The hole numbers this round PLAYED, in play order — `[10..18]` for a back
   * nine. Never the course's eighteen: a nine's card is a nine's card.
   */
  holeNumbers: readonly number[]
  /** The course's own card — par and stroke index per hole. May be empty. */
  courseHoles: readonly CourseHole[]
  players: readonly RoundScorecardPlayer[]
  /** The course, named in each table's caption. */
  courseName: string
  /** Ready media per hole. Omitted when this card has no gallery context. */
  mediaCounts?: ReadonlyMap<number, number>
  className?: string
}

/**
 * The round's card, in full: every player's every hole.
 *
 * ## Two nines, never one eighteen
 *
 * An eighteen-column table is a table nobody can read on a phone and nobody
 * recognises on a desk — the paper card is two nines with an Out and an In, so
 * this is too. A NINE-hole round renders its single nine and calls the total
 * what it is. Each table scrolls inside its OWN box (`overflow-x-auto`), which
 * is what keeps the page body from ever scrolling sideways: wrap don't hide.
 *
 * ## What the cells say, and what carries it
 *
 * A cell under par takes a RING and a cell over par takes the flag-deep tint,
 * exactly as the design card draws them. Neither is the only channel for
 * anything: the stroke is printed in the cell and the hole's par is printed in
 * its own quiet row directly above, so a reader who sees no colour at all still
 * has both numbers. The emphasis is emphasis.
 *
 * ## Where the numbers come from
 *
 * Par and stroke index are the COURSE's, read off the card the round carries;
 * a course with no seeded holes simply loses those two rows rather than
 * printing a row of dashes. Player totals are summed over the holes of that
 * nine that actually carry a stroke — a card still being written totals what
 * has been written, and a nine with nothing on it reads "—" rather than 0.
 *
 * The PAR total is the other kind of number and takes the other rule: ALL OR
 * NOTHING (`parTotal`). A player's running total is a fact about what they have
 * played so far; a nine's par is a property of the nine, and one uncarded hole
 * makes it unknowable rather than smaller.
 */
export function RoundScorecard({
  holeNumbers,
  courseHoles,
  players,
  courseName,
  mediaCounts,
  className,
}: RoundScorecardProps) {
  const nines = chunkIntoNines(holeNumbers)
  const parFor = new Map(courseHoles.map((hole) => [hole.number, hole.par]))
  const strokeIndexFor = new Map(courseHoles.map((hole) => [hole.number, hole.stroke_index]))
  const seated = [...players].sort((a, b) => a.position - b.position)

  return (
    <div className={cn('flex flex-col gap-5', className)} data-round-scorecard="">
      {nines.map((nine, index) => (
        <div
          key={nine[0] ?? index}
          className="overflow-x-auto rounded-club-lg border border-sage-soft bg-parchment px-5 pb-2 pt-5"
          data-scorecard-nine={index === 0 ? 'out' : 'in'}
        >
          <table className="w-full border-collapse tabular-nums">
            <caption className="mb-3.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep">
              {captionFor(nine, nines.length, index, courseName)}
            </caption>
            <thead>
              <tr>
                <Head>Hole</Head>
                {nine.map((number) => (
                  <Head key={number}>
                    {number}
                    <MediaCountMark count={mediaCounts?.get(number) ?? 0} />
                  </Head>
                ))}
                <Head total>{totalLabel(nines.length, index)}</Head>
              </tr>
            </thead>
            <tbody>
              {parFor.size > 0 && (
                <QuietRow
                  label="Par"
                  nine={nine}
                  value={(number) => parFor.get(number) ?? '—'}
                  total={parTotal(nine.map((number) => parFor.get(number)))}
                />
              )}
              {strokeIndexFor.size > 0 && (
                <QuietRow
                  label="Stroke index"
                  nine={nine}
                  value={(number) => strokeIndexFor.get(number) ?? '—'}
                  // A stroke index is a rank over the whole course. Nine of
                  // them added together answers no question, so the cell is
                  // empty rather than wrong.
                  total=""
                />
              )}
              {seated.map((player) => (
                <PlayerRow key={player.id} player={player} nine={nine} parFor={parFor} />
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}

function MediaCountMark({ count }: { count: number }) {
  if (count === 0) return null

  return (
    <span
      className="relative ml-1 inline-flex items-center text-[11px] leading-none text-fairway"
      data-media-count={count}
    >
      <span aria-hidden="true">●</span>
      <span className="sr-only">
        {`; ${count} media ${count === 1 ? 'item' : 'items'}`}
      </span>
    </span>
  )
}

function PlayerRow({
  player,
  nine,
  parFor,
}: {
  player: RoundScorecardPlayer
  nine: readonly number[]
  parFor: Map<number, number>
}) {
  const strokesFor = new Map(
    player.scores.filter((score) => score.strokes !== null).map((score) => [score.hole_number, score.strokes!]),
  )

  return (
    <tr
      className={cn(player.isYou && 'bg-sage-soft/40', player.declined && 'text-ink-soft')}
      data-scorecard-player={player.id}
    >
      <th
        scope="row"
        className="whitespace-nowrap border-b border-brass/25 px-3.5 py-3 text-left text-[13px] font-semibold text-ink"
      >
        {/* The tint cannot carry "this row is yours" for a reader who sees no
            pixels, so the row says so in words as well. */}
        {player.isYou && <span className="sr-only">Your card: </span>}
        {player.displayName}
        {player.guest && <RowChip>Guest</RowChip>}
        {player.declined && <RowChip>Declined</RowChip>}
      </th>
      {nine.map((number) => {
        const strokes = strokesFor.get(number)
        const par = parFor.get(number)
        return (
          <Cell key={number} strokes={strokes} par={par} declined={player.declined} />
        )
      })}
      <td className="border-b border-l border-brass/25 px-3.5 py-3 text-center font-display text-sm font-semibold tabular-nums text-pine">
        {sumOf(nine.map((number) => strokesFor.get(number)))}
      </td>
    </tr>
  )
}

function Cell({
  strokes,
  par,
  declined,
}: {
  strokes: number | undefined
  par: number | undefined
  declined?: boolean
}) {
  const toPar = strokes !== undefined && par !== undefined ? strokes - par : null

  return (
    <td
      data-shape={toPar === null ? undefined : shapeOf(toPar)}
      className={cn(
        'border-b border-brass/25 px-3.5 py-3 text-center text-[13px] tabular-nums',
        declined ? 'text-ink-soft' : 'text-ink',
        // Under par takes the club's ring — a SHAPE, drawn as an outline so it
        // never fills a cell a member has to read a number out of.
        toPar !== null && toPar < 0 && 'rounded-full outline outline-[1.5px] -outline-offset-[3px] outline-fairway',
        toPar !== null && toPar > 0 && 'text-flag-deep',
      )}
    >
      {/* A hole with no stroke recorded is a hole nobody entered, never a zero. */}
      {strokes ?? '—'}
    </td>
  )
}

/** A chip beside a name — what the wire says about the seat, never a judgement. */
function RowChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="ml-2 whitespace-nowrap rounded-club-sm border border-ink-soft/35 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.12em] text-ink-soft">
      {children}
    </span>
  )
}

function QuietRow({
  label,
  nine,
  value,
  total,
}: {
  label: string
  nine: readonly number[]
  value: (number: number) => React.ReactNode
  total: React.ReactNode
}) {
  return (
    <tr className="text-ink-soft" data-scorecard-row={label.toLowerCase().replace(/\s+/g, '-')}>
      <th
        scope="row"
        className="whitespace-nowrap border-b border-brass/25 px-3.5 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep"
      >
        {label}
      </th>
      {nine.map((number) => (
        <td key={number} className="border-b border-brass/25 px-3.5 py-2.5 text-center text-[11.5px] tabular-nums">
          {value(number)}
        </td>
      ))}
      <td className="border-b border-l border-brass/25 px-3.5 py-2.5 text-center text-[11.5px] tabular-nums">
        {total}
      </td>
    </tr>
  )
}

function Head({ children, total }: { children: React.ReactNode; total?: boolean }) {
  return (
    <th
      scope="col"
      className={cn(
        'border-b border-brass px-3.5 pb-2 text-center text-[9.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep',
        total && 'border-l',
      )}
    >
      {children}
    </th>
  )
}

/** Nine at a time, in play order — `[10..18]` chunks exactly as `[1..9]` does. */
function chunkIntoNines(holeNumbers: readonly number[]): number[][] {
  const nines: number[][] = []
  for (let start = 0; start < holeNumbers.length; start += 9) {
    nines.push(holeNumbers.slice(start, start + 9))
  }
  return nines
}

/**
 * WHAT HAS BEEN WRITTEN, totalled — a card still being kept totals the holes
 * that carry a stroke, and a nine with nothing at all on it reads "—" rather
 * than 0.
 *
 * That is right for STROKES and wrong for par, which is why par has a rule of
 * its own below: a player's total is a running fact about their own card, so a
 * partial one is honest and useful, whereas a nine's par is a property of the
 * nine that a missing hole makes unknowable rather than smaller.
 */
function sumOf(values: readonly (number | undefined)[]): string {
  const known = values.filter((value): value is number => value !== undefined)
  if (known.length === 0) return '—'
  return String(known.reduce((total, value) => total + value, 0))
}

/**
 * ALL OR NOTHING, like the server's own `round_par`: one hole of the nine
 * missing its par and the sum is not a smaller truth, it is a wrong one — the
 * one thing anybody does with this number is take a gross away from it, and
 * "level" against eight holes' par is a lie about a round.
 *
 * A partially carded course therefore prints the pars it holds hole by hole
 * and NO total, which is the same refusal the server makes rather than
 * computing a substitute.
 */
function parTotal(pars: readonly (number | undefined)[]): string {
  const known = pars.filter((par): par is number => par !== undefined)
  if (known.length === 0 || known.length !== pars.length) return '—'
  return String(known.reduce((total, par) => total + par, 0))
}

function captionFor(
  nine: readonly number[],
  nines: number,
  index: number,
  courseName: string,
): string {
  const range = `holes ${nine[0]} to ${nine[nine.length - 1]}`
  if (nines === 1) return `${courseName} — ${range}`
  return `${index === 0 ? 'Out' : 'In'} — ${range} · ${courseName}`
}

/** The name of a hole against its par — for tests and for nothing visual. */
function shapeOf(toPar: number): string {
  if (toPar <= -2) return 'eagle-or-better'
  if (toPar === -1) return 'birdie'
  if (toPar === 0) return 'par'
  if (toPar === 1) return 'bogey'
  if (toPar === 2) return 'double'
  return 'triple-or-worse'
}
